import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, take, tap } from 'rxjs/operators';
import { RestService } from '../../../../shared/services/rest.service';
import { ContactRepository } from '../../../contact/states/contact.repository';
import { AnimalDocumentModel } from '../../models/animal-document.model';
import { AnimalMediaModel } from '../../models/animal-media.model';
import { AnimalMedicalConditionModel } from '../../models/medical/animal-medical-condition.model';
import { AnimalMedicalRecordModel } from '../../models/medical/animal-medical-record.model';
import { AnimalPreventiveTreatmentModel } from '../../models/medical/animal-preventive-treatment.model';
import { AnimalQuarantineModel } from '../../models/medical/animal-quarantine.model';
import { AnimalVeterinaryContactModel } from '../../models/medical/animal-veterinary-contact.model';
import { DueTreatmentStatus } from '../../models/medical/medical.enums';
import { DueTreatmentModel } from '../../models/medical/due-treatment.model';
import { todayIsoDate } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalRepository } from '../animal.repository';
import { AnimalMedicalConditionRepository } from './animal-medical-condition.repository';
import { AnimalMedicalRecordRepository } from './animal-medical-record.repository';
import { AnimalPreventiveTreatmentRepository } from './animal-preventive-treatment.repository';
import { AnimalQuarantineRepository } from './animal-quarantine.repository';
import { AnimalVeterinaryContactRepository } from './animal-veterinary-contact.repository';
import { DueTreatmentRepository } from './due-treatment.repository';
import {
  activeConditions,
  activeOrPlannedQuarantines,
  correctedByMap,
  correctionHistoryByHeadId,
  filesByRecordId,
  latestTreatmentPerGroup,
  medicalCounts,
  positionsByRecordId,
  primaryVeterinaryContact,
  toMedicalFiles
} from './animal-medical.selectors';

export interface AnimalMedicalState {
  animalId: number | null;
  loading: boolean;
  loaded: boolean;
  records: AnimalMedicalRecordModel[];
  treatments: AnimalPreventiveTreatmentModel[];
  conditions: AnimalMedicalConditionModel[];
  quarantines: AnimalQuarantineModel[];
  veterinaryContacts: AnimalVeterinaryContactModel[];
  documents: AnimalDocumentModel[];
  images: AnimalMediaModel[];
  videos: AnimalMediaModel[];
  dueTreatments: DueTreatmentModel[];
  userNames: Record<number, string>;
}

const initialState: AnimalMedicalState = {
  animalId: null,
  loading: false,
  loaded: false,
  records: [],
  treatments: [],
  conditions: [],
  quarantines: [],
  veterinaryContacts: [],
  documents: [],
  images: [],
  videos: [],
  dueTreatments: [],
  userNames: {},
};

/** Medizinische Daten des gerade geöffneten Tiers. */
export const animalMedicalStore = createStore(
  { name: 'animalMedical' },
  withProps<AnimalMedicalState>(initialState)
);

@Injectable({ providedIn: 'root' })
export class AnimalMedicalRepository {
  private _userNames$: Observable<Record<number, string>> | null = null;
  private _pending: Observable<void> | null = null;

  readonly animalId$ = animalMedicalStore.pipe(select(state => state.animalId));
  readonly loading$ = animalMedicalStore.pipe(select(state => state.loading));
  readonly loaded$ = animalMedicalStore.pipe(select(state => state.loaded));
  readonly records$ = animalMedicalStore.pipe(select(state => state.records));
  readonly treatments$ = animalMedicalStore.pipe(select(state => state.treatments));
  readonly conditions$ = animalMedicalStore.pipe(select(state => state.conditions));
  readonly quarantines$ = animalMedicalStore.pipe(select(state => state.quarantines));
  readonly veterinaryContacts$ = animalMedicalStore.pipe(select(state => state.veterinaryContacts));
  readonly dueTreatments$ = animalMedicalStore.pipe(select(state => state.dueTreatments));
  readonly userNames$ = animalMedicalStore.pipe(select(state => state.userNames));

  readonly files$ = animalMedicalStore.pipe(
    select(state => toMedicalFiles(state.documents, state.images, state.videos))
  );
  readonly positionsByRecordId$ = animalMedicalStore.pipe(
    select(state => positionsByRecordId(state.treatments, state.conditions))
  );
  readonly filesByRecordId$ = this.files$.pipe(map(files => filesByRecordId(files)));
  readonly correctedByMap$ = animalMedicalStore.pipe(select(state => correctedByMap(state.records)));
  readonly correctionHistory$ = animalMedicalStore.pipe(select(state => correctionHistoryByHeadId(state.records)));
  readonly activeConditions$ = animalMedicalStore.pipe(select(state => activeConditions(state.conditions)));
  readonly activeOrPlannedQuarantines$ = animalMedicalStore.pipe(
    select(state => activeOrPlannedQuarantines(state.quarantines, todayIsoDate()))
  );
  readonly latestTreatmentPerGroup$ = animalMedicalStore.pipe(select(state => latestTreatmentPerGroup(state.treatments)));
  readonly primaryVeterinaryContact$ = animalMedicalStore.pipe(
    select(state => primaryVeterinaryContact(state.veterinaryContacts))
  );
  readonly counts$ = animalMedicalStore.pipe(
    select(state => medicalCounts({
      records: state.records,
      treatments: state.treatments,
      conditions: state.conditions,
      quarantines: state.quarantines,
      files: toMedicalFiles(state.documents, state.images, state.videos),
      today: todayIsoDate(),
      dueAlerts: state.dueTreatments.filter(due =>
        due.status === DueTreatmentStatus.OVERDUE || due.status === DueTreatmentStatus.DUE
      ).length,
    }))
  );

  constructor(
    private readonly _restService: RestService,
    private readonly _contactRepository: ContactRepository,
    private readonly _animalRepository: AnimalRepository,
    private readonly _recordRepository: AnimalMedicalRecordRepository,
    private readonly _treatmentRepository: AnimalPreventiveTreatmentRepository,
    private readonly _conditionRepository: AnimalMedicalConditionRepository,
    private readonly _quarantineRepository: AnimalQuarantineRepository,
    private readonly _veterinaryContactRepository: AnimalVeterinaryContactRepository,
    private readonly _dueTreatmentRepository: DueTreatmentRepository
  ) {}

  getSnapshot(): AnimalMedicalState {
    return animalMedicalStore.getValue();
  }

  /** Lädt alles für ein Tier. Liegt das Tier bereits im Store, passiert ohne `force` nichts. */
  loadAll(animalId: number, force = false): Observable<void> {
    const current = animalMedicalStore.getValue();
    if (!force && current.animalId === animalId && (current.loaded || current.loading)) {
      return this._pending ?? of(undefined);
    }

    this._contactRepository.loadAllIfNeeded();

    if (current.animalId !== animalId) {
      animalMedicalStore.update(() => ({ ...initialState, userNames: current.userNames, animalId, loading: true }));
    } else {
      animalMedicalStore.update(state => ({ ...state, loading: true }));
    }

    const request$ = forkJoin({
      records: this._recordRepository.getByAnimalId(animalId),
      treatments: this._treatmentRepository.getByAnimalId(animalId),
      conditions: this._conditionRepository.getByAnimalId(animalId),
      quarantines: this._quarantineRepository.getByAnimalId(animalId),
      veterinaryContacts: this._veterinaryContactRepository.getByAnimalId(animalId),
      documents: this.loadList<AnimalDocumentModel>(`/animal_documents?animal.id=${animalId}`),
      images: this.loadList<AnimalMediaModel>(`/animal_images?animal.id=${animalId}`),
      videos: this.loadList<AnimalMediaModel>(`/animal_videos?animal.id=${animalId}`),
      dueTreatments: this._dueTreatmentRepository.getByAnimal(animalId),
      userNames: this.loadUserNames(),
    }).pipe(
      tap(result => {
        // Ein späterer Wechsel auf ein anderes Tier darf nicht überschrieben werden.
        if (animalMedicalStore.getValue().animalId !== animalId) return;
        animalMedicalStore.update(state => ({
          ...state,
          ...result,
          documents: result.documents.filter(document => refId(document.animalMedicalRecord) !== null),
          images: result.images.filter(image => refId(image.medicalRecord) !== null),
          videos: result.videos.filter(video => refId(video.medicalRecord) !== null),
          loading: false,
          loaded: true,
        }));
      }),
      map(() => undefined),
      finalize(() => {
        this._pending = null;
        if (animalMedicalStore.getValue().animalId === animalId) {
          animalMedicalStore.update(state => ({ ...state, loading: false }));
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this._pending = request$;
    return request$;
  }

  /** Nach dem Speichern: Store und Tier neu laden (Gewicht, Vermittlungssperre, aktuelle Beobachtungen). */
  reload(): Observable<void> {
    const animalId = animalMedicalStore.getValue().animalId;
    if (!animalId) return of(undefined);

    this._animalRepository.getById(animalId, false).pipe(take(1)).subscribe();
    return this.loadAll(animalId, true);
  }

  /** Nur die Haustierärzte neu laden (nach dem Pflegedialog). */
  reloadVeterinaryContacts(): Observable<AnimalVeterinaryContactModel[]> {
    const animalId = animalMedicalStore.getValue().animalId;
    if (!animalId) return of([]);
    return this._veterinaryContactRepository.getByAnimalId(animalId).pipe(
      tap(veterinaryContacts => {
        if (animalMedicalStore.getValue().animalId !== animalId) return;
        animalMedicalStore.update(state => ({ ...state, veterinaryContacts }));
      })
    );
  }

  userName(userId: number | null): string | null {
    if (userId === null) return null;
    return animalMedicalStore.getValue().userNames[userId] ?? null;
  }

  private loadList<T>(path: string): Observable<T[]> {
    return this._restService.httpGet(path).pipe(
      take(1),
      map(response => (Array.isArray(response) ? response as T[] : [])),
      catchError(() => of([] as T[]))
    );
  }

  /** `/users/all` einmalig laden: `creator` kommt nur als Id. */
  private loadUserNames(): Observable<Record<number, string>> {
    if (!this._userNames$) {
      this._userNames$ = this._restService.httpGet('/users/all').pipe(
        take(1),
        map((users: { id: number; displayName?: string; name?: string }[] | null) => {
          const names: Record<number, string> = {};
          (users ?? []).forEach(user => {
            const name = user.displayName || user.name;
            if (user.id && name) names[user.id] = name;
          });
          return names;
        }),
        catchError(() => {
          this._userNames$ = null;
          return of({} as Record<number, string>);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this._userNames$;
  }
}
